import axios, { AxiosRequestConfig } from "axios"
import { API_BASE_URL, REQUEST_TIMEOUT, USES_SUBDOMAIN } from "configs/AppConfig"
import { PATIENT_CSRF_TOKEN, PORTAL_CSRF_TOKEN, PORTAL_EXPIRES, USER } from "constants/StorageConstants"
import { getCsrfToken } from "hooks/auth"
import { notification } from "hooks/toast"
import { publicAxiosHooks } from "routes/public/components/Public/hooks"

import { ErrorCode } from "@shared/enums/errorCode"

import { onResponseRejected } from "./onResponseRejected"

/**
 * Disable ESlint rules as the error response type is unclear
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

const portalLoginPath = "/auth/login"
const getPatientLoginPath = () => `/${USES_SUBDOMAIN ? "" : publicAxiosHooks.practiceSlug}`

// Define default axios configuration
axios.defaults.baseURL = API_BASE_URL
axios.defaults.timeout = REQUEST_TIMEOUT

// Create public, patient and authenticated instances
const axiosPublic = axios.create({ withCredentials: true }) // Axios bug that all instances needs to have withCredentials
const axiosPatient = axios.create({ withCredentials: true })
const axiosAuth = axios.create({ withCredentials: true })

// Request interceptors
const onRequestRejected = (error: any) => {
  // Show notification for request error
  notification.error({
    message: "Error",
  })
  return Promise.reject(error)
}

// TODO: should return AxiosRequestConfig, but currently TypeScript issue: https://github.com/axios/axios/issues/5494
const requestConfig = (config: AxiosRequestConfig, loginRoute: string, csrfToken: string | null | undefined): any => {
  if (!config.headers) {
    config.headers = {}
  }

  if (csrfToken) {
    config.headers = { "X-CSRF-TOKEN": csrfToken }
  } else {
    console.error("Missing csrfToken")
    window.location.href = loginRoute
  }

  return config
}

axiosPublic.interceptors.request.use((config) => config, onRequestRejected)
axiosPatient.interceptors.request.use(
  (config) => requestConfig(config, getPatientLoginPath(), localStorage.getItem(PATIENT_CSRF_TOKEN)),
  onRequestRejected
)
axiosAuth.interceptors.request.use(
  (config) => requestConfig(config, portalLoginPath, getCsrfToken()),
  onRequestRejected
)

axiosPublic.interceptors.response.use(
  (response) => response,
  (error) => onResponseRejected(error, undefined)
)
axiosPatient.interceptors.response.use(
  (response) => response,
  (error) =>
    onResponseRejected(error, (error) => {
      if (
        ![ErrorCode.PatientAccountInvalidCredentials, ErrorCode.PatientAccountInvalidCredentialsMfa].includes(
          error.response?.data?.errorCode
        )
      ) {
        // Invalid credentials is handled by login view, so this is probably expired token
        publicAxiosHooks.patientLogoutAutomatic()
      }
    })
)
axiosAuth.interceptors.response.use(
  (response) => response,
  (error) =>
    onResponseRejected(error, (error) => {
      // Remove token and redirect
      localStorage.removeItem(USER)
      localStorage.removeItem(PORTAL_CSRF_TOKEN)
      localStorage.removeItem(PORTAL_EXPIRES)
      sessionStorage.removeItem(USER)
      sessionStorage.removeItem(PORTAL_CSRF_TOKEN)
      sessionStorage.removeItem(PORTAL_EXPIRES)
      if (
        ![ErrorCode.LoginInvalidCredentials, ErrorCode.LoginInvalidCredentialsTotp].includes(
          error.response?.data?.errorCode
        )
      ) {
        // Invalid credentials is handled by login view, so this is probably expired token
        window.location.href = `${portalLoginPath}?loggedOut`
      }
    })
)

export { axiosAuth, axiosPatient, axiosPublic }
